import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fetchPage, blobUrl, getSourceLinks, resolveEntity, type SynapsePage, type SourceLink } from "../api";


/**
 * Sidebar synapse preview. Beyond rendering the page body, surfaces:
 *   - Raw-source provenance, resolved by the Worker per archetype:
 *     prose → the R2 corpus link; code → the file on GitHub.
 *   - Entity refs as small chips
 *   - Copy-as-markdown
 *
 * When opened from a citation that carries a `#section` anchor, scrolls the
 * body to the matching heading.
 */

export function SynapsePreview({
  instance, pageId, anchor, onOpenPage,
}: {
  instance: string;
  pageId: string | null;
  anchor?: string;
  /** Open another synapse page in this panel (e.g. an entity's registry page). */
  onOpenPage?: (pageId: string) => void;
}) {
  const [page, setPage] = useState<SynapsePage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  // Archetype-aware source links from the Worker (R2 for prose, GitHub for code).
  const [srcLinks, setSrcLinks] = useState<SourceLink[]>([]);
  // entity ref → its synapse page id, for the entities that have a page. Others
  // stay plain tags, so an entity chip is only clickable when it leads somewhere.
  const [entityPages, setEntityPages] = useState<Record<string, string>>({});
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pageId) {
      setPage(null);
      setErr(null);
      return;
    }
    setLoading(true);
    setErr(null);
    setSrcLinks([]);
    fetchPage(instance, pageId)
      .then(p => { setPage(p); setLoading(false); })
      .catch(e => { setErr((e as Error).message); setLoading(false); });
    getSourceLinks(instance, pageId)
      .then(r => setSrcLinks(r.sources))
      .catch(() => setSrcLinks([]));      // leave empty → links hidden
  }, [instance, pageId]);

  // Resolve which of the page's entities have a synapse page (so only those
  // render as clickable chips). Runs once per loaded page.
  useEffect(() => {
    setEntityPages({});
    const ents = ((page?.header.entities as string[]) || []).slice(0, 12);
    if (ents.length === 0) return;
    let alive = true;
    Promise.allSettled(ents.map(async ref => {
      const p = await resolveEntity(instance, ref);
      return p ? ([ref, p.id] as const) : null;
    })).then(results => {
      if (!alive) return;
      const map: Record<string, string> = {};
      for (const r of results) if (r.status === "fulfilled" && r.value) map[r.value[0]] = r.value[1];
      setEntityPages(map);
    });
    return () => { alive = false; };
  }, [instance, page]);

  // Scroll to the cited section once the body for this page is on screen.
  useEffect(() => {
    if (!anchor || !page) return;
    const el = bodyRef.current?.querySelector(`[id="${CSS.escape(anchor)}"]`);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [anchor, page]);

  if (!pageId) {
    return (
      <div className="empty">
        Click a citation in the answer to preview the synapse page here.
      </div>
    );
  }
  if (loading) return <div className="empty">loading {pageId}…</div>;
  if (err) return <div className="empty">error: {err}</div>;
  if (!page) return <div className="empty">no page</div>;

  const title = (page.header.title as string) || page.id;
  const kind = (page.header.kind as string) || "?";
  const authorityTier = (page.header.authority_tier as string) || "";
  const entities = (page.header.entities as string[]) || [];
  const primary = srcLinks[0];
  // Footnote citations → clickable reference chips; body stripped of the
  // footnote machinery so remark-gfm no longer emits its backref section.
  const { refs, cleanBody } = extractReferences(page.body, instance, srcLinks);

  const copyMarkdown = async () => {
    const fm = "---\n" + Object.entries(page.header)
      .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
      .join("\n") + "\n---\n\n";
    await navigator.clipboard.writeText(fm + page.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <div className="preview-header">
        <h3>{title}</h3>
        <div className="preview-actions">
          <button
            onClick={copyMarkdown}
            title="Copy full markdown (frontmatter + body)"
            className="action-btn"
          >
            {copied ? "✓ copied" : "Copy md"}
          </button>
        </div>
      </div>
      <div className="meta">
        {page.id} · {kind}
        {authorityTier && ` · ${authorityTier}`}
        {primary && (
          <> · <a href={primary.url} target="_blank" rel="noreferrer" title={primary.label}>
            {sourceVerb(primary.kind)} ↗
          </a></>
        )}
      </div>

      {srcLinks.length > 0 && (
        <div className="sources">
          <div className="sources-label">Sources ({srcLinks.length})</div>
          {srcLinks.map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noreferrer" className="source" title={s.url}>
              <span className="source-uri">{s.label}</span>
              <span className="source-ts">{s.kind === "github" ? "GitHub" : s.kind === "external" ? "link" : "raw"}</span>
            </a>
          ))}
        </div>
      )}

      {entities.length > 0 && (
        <div className="entities">
          <div className="entities-label">Entities ({entities.length})</div>
          <div className="entity-chips">
            {entities.slice(0, 12).map(e => {
              const target = entityPages[e];
              return target ? (
                <button key={e} className="entity-chip entity-link"
                        title={`Open ${shortenEntity(e)}`} onClick={() => onOpenPage?.(target)}>
                  {shortenEntity(e)}
                </button>
              ) : (
                <span key={e} className="entity-chip" title={e}>
                  {shortenEntity(e)}
                </span>
              );
            })}
            {entities.length > 12 && (
              <span className="entity-chip more">+{entities.length - 12}</span>
            )}
          </div>
        </div>
      )}

      <div className="body" ref={bodyRef}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Slugged heading ids so a citation's #section anchor can scroll here.
            h1: h(1), h2: h(2), h3: h(3), h4: h(4), h5: h(5), h6: h(6),
            // Corpus-path mentions in the body → clickable raw-blob links.
            p({ children }) {
              return <p>{linkifyCorpusPaths(children, instance)}</p>;
            },
            li({ children }) {
              return <li>{linkifyCorpusPaths(children, instance)}</li>;
            },
          }}
        >
          {cleanBody}
        </ReactMarkdown>
      </div>

      {refs.length > 0 && (
        <div className="references">
          <div className="references-label">References ({refs.length})</div>
          <div className="reference-chips">
            {refs.map((r, i) => r.href ? (
              <a key={`${r.id}-${i}`} className="reference-chip" href={r.href}
                 target="_blank" rel="noreferrer" title={r.title}>
                {r.label}
              </a>
            ) : (
              <span key={`${r.id}-${i}`} className="reference-chip" title={r.title}>
                {r.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


interface Reference {
  id: string;
  label: string;
  href?: string;
  title: string;
}

/**
 * Pull GFM footnote definitions out of the body and turn them into clickable
 * reference chips. A definition is `[^id]: <source> § <section>`, where the
 * source and/or section may themselves be markdown links. Returns the parsed
 * references plus a body with every footnote artifact removed — the literal
 * "Footnotes" heading, the definition lines, and the inline `[^id]` markers —
 * so remark-gfm emits no footnotes section.
 */
function extractReferences(
  body: string, instance: string, srcLinks: SourceLink[],
): { refs: Reference[]; cleanBody: string } {
  const refs: Reference[] = [];
  const defRe = /^[ \t]*\[\^([^\]]+)\]:[ \t]*(.*)$/gm;
  let m: RegExpExecArray | null;
  while ((m = defRe.exec(body)) !== null) {
    refs.push(parseReference(m[1], m[2].trim(), instance, srcLinks));
  }
  if (refs.length === 0) return { refs: [], cleanBody: body };

  const cleanBody = body
    .replace(/^[ \t]*\[\^[^\]]+\]:[ \t]*.*$/gm, "")     // definition lines
    .replace(/^#{1,6}[ \t]*Footnotes[ \t]*$/gim, "")    // literal "Footnotes" heading
    .replace(/^[ \t]*\*\*Footnotes\*\*[ \t]*$/gim, "")
    .replace(/\[\^[^\]]+\]/g, "")                        // inline [^id] markers
    .replace(/[ \t]+$/gm, "")                            // trailing spaces left behind
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
  return { refs, cleanBody };
}

function parseReference(
  id: string, raw: string, instance: string, srcLinks: SourceLink[],
): Reference {
  // collect any markdown links [text](url)
  const links: { text: string; url: string }[] = [];
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lm: RegExpExecArray | null;
  while ((lm = linkRe.exec(raw)) !== null) links.push({ text: lm[1], url: lm[2] });

  // display text: drop link syntax + backticks
  const cleaned = raw
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/`/g, "")
    .trim();

  let source = cleaned, section = "";
  const sIdx = cleaned.indexOf("§");
  if (sIdx >= 0) {
    source = cleaned.slice(0, sIdx).trim();
    section = cleaned.slice(sIdx + 1).trim();
  }
  const label = section || source || id;

  // href: prefer an explicit link (the "§ Source" link on code pages), else map
  // the source uri to a resolved source link, else the raw-blob route.
  let href: string | undefined = links.length ? links[links.length - 1].url : undefined;
  if (!href) {
    const uri = source.split(/\s+/)[0].replace(/[*/]+$/g, "");
    const hit = uri.length >= 4
      ? srcLinks.find(s =>
          s.uri === uri || s.label === uri ||
          s.uri.endsWith(uri) || s.label.endsWith(uri) ||
          uri.endsWith(s.uri) || uri.endsWith(s.label))
      : undefined;
    if (hit) href = hit.url;
    else if (/^(?:code|spec|sibling|handbook|raw)\/[\w.\-/]+\.\w+$/.test(uri)) href = blobUrl(instance, uri);
  }
  return { id, label, href, title: cleaned || id };
}


function sourceVerb(kind: SourceLink["kind"]): string {
  return kind === "github" ? "view on GitHub" : kind === "external" ? "open source" : "view raw";
}


/** Heading component factory — adds a slug id matching the citation anchor. */
function h(level: 1 | 2 | 3 | 4 | 5 | 6) {
  const Tag = `h${level}` as const;
  return function Heading({ children }: { children?: React.ReactNode }) {
    return <Tag id={slugify(nodeText(children))}>{children}</Tag>;
  };
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function nodeText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (node && typeof node === "object" && "props" in node) {
    return nodeText((node as { props?: { children?: React.ReactNode } }).props?.children);
  }
  return "";
}


/** Walk markdown children; in any string fragment, find substrings that
 *  look like corpus paths (e.g. `code/booking.service.md`) and wrap
 *  them in <a target="_blank"> links to the R2 blob. */
function linkifyCorpusPaths(children: React.ReactNode, instance: string): React.ReactNode {
  const corpusPathRe = /\b((?:code|spec|sibling|handbook|raw)\/[a-zA-Z0-9._-]+\.(?:md|ts|tsx|js|json|pdf|html|txt))\b/g;

  function walk(node: React.ReactNode, idx: number): React.ReactNode {
    if (typeof node === "string") {
      const parts: React.ReactNode[] = [];
      let last = 0;
      let m: RegExpExecArray | null;
      corpusPathRe.lastIndex = 0;
      while ((m = corpusPathRe.exec(node)) !== null) {
        const before = node.slice(last, m.index);
        if (before) parts.push(before);
        parts.push(
          <a
            key={`${idx}-${m.index}`}
            href={blobUrl(instance, m[1])}
            target="_blank"
            rel="noreferrer"
            className="corpus-ref"
            title={`open raw blob: ${m[1]}`}
          >
            {m[1]}
          </a>,
        );
        last = m.index + m[1].length;
      }
      if (last < node.length) parts.push(node.slice(last));
      return parts.length > 1 ? parts : node;
    }
    if (Array.isArray(node)) {
      return node.map((c, i) => <span key={i}>{walk(c, i)}</span>);
    }
    return node;
  }
  return walk(children, 0);
}


function shortenEntity(ref: string): string {
  // entity:type/slug → type/slug (drop the prefix for visual density)
  return ref.replace(/^entity:/, "");
}
